type BooleanFlag = {
  key: string;
  type: 'boolean';
  label: string;
  description: string;
  defaultValue: boolean;
  owner?: string;
  createdAt?: string;
  expiresAt?: string;
};

type VariantFlag<V extends ReadonlyArray<string> = ReadonlyArray<string>> = {
  key: string;
  type: 'variant';
  label: string;
  description: string;
  variants: V;
  defaultValue: V[number];
  owner?: string;
  createdAt?: string;
  expiresAt?: string;
};

type FeatureFlag = BooleanFlag | VariantFlag;

export const featureFlags = [
  {
    key: 'apartment-reviews',
    type: 'boolean',
    label: 'Apartment Reviews',
    description: 'Shows the apartment reviews integration shell. Provider panels are still in development.',
    defaultValue: false,
    owner: 'hotpads-web',
  },
  {
    key: 'auth-url-privacy',
    type: 'boolean',
    label: 'Auth URL Privacy',
    description: 'Uses cookie handoffs instead of email and reset tokens in browser URLs.',
    defaultValue: false,
  },
  {
    key: 'mock-inquiry',
    type: 'boolean',
    label: 'Mock Inquiry',
    description: 'Skips the real inquiry API and returns a mock success response. For testing contact form flows.',
    defaultValue: false,
  },
  {
    key: 'mock-book-tour',
    type: 'boolean',
    label: 'Mock Book Tour',
    description: 'Skips tour-booking API side effects and returns mock success responses. For testing tour flows.',
    defaultValue: false,
  },
  {
    key: 'header-version',
    type: 'variant',
    label: 'Header Version (Example / NOOP)',
    description: 'Which header design to render',
    variants: ['default', 'floating', 'compact'] as const,
    defaultValue: 'default',
  },
  {
    key: 'dotmap-renderer',
    type: 'variant',
    label: 'DotMap Renderer',
    description:
      'How the SRP map density dots render: a single canvas OverlayView vs one AdvancedMarkerElement per dot (HP-8328).',
    variants: ['canvas', 'advanced-marker'] as const,
    defaultValue: 'canvas',
    owner: 'daves@zillowgroup.com',
    createdAt: '2026-07-06',
    expiresAt: '2026-10-01',
  },
  {
    key: 'dotmap-thinning',
    type: 'variant',
    label: 'DotMap Thinning',
    description:
      'Screen-grid dot thinning policy for the canvas dotmap renderer (HP-8328). Off by default; enable via ?ff_dotmap-thinning=auto or screen-grid.',
    variants: ['off', 'auto', 'screen-grid'] as const,
    defaultValue: 'off',
    owner: 'daves@zillowgroup.com',
    createdAt: '2026-07-06',
    expiresAt: '2026-10-01',
  },
] as const satisfies ReadonlyArray<FeatureFlag>;

export type FeatureFlagKey = (typeof featureFlags)[number]['key'];

export type BooleanFlagKey = Extract<(typeof featureFlags)[number], { type: 'boolean' }>['key'];

export type VariantFlagKey = Extract<(typeof featureFlags)[number], { type: 'variant' }>['key'];

export type VariantValuesFor<K extends VariantFlagKey> = Extract<
  (typeof featureFlags)[number],
  { key: K }
>['variants'][number];

export function getFlagDefinition(key: FeatureFlagKey) {
  return featureFlags.find((f) => f.key === key);
}
